import { defineStore } from "pinia";
import { computed, reactive, ref, type ComputedRef, type Ref } from "vue";
import { useDialogStore } from "./dialogStore";
import { useLoginPageStore, type LoginView } from "./loginPageStore";
import { usePanelLayoutStore } from "./layoutStore";

type Theme = "light" | "dark";

export interface CurrentPage {
  name: string;
  title: string;
  breadcrumb: Array<{ name: string; path?: string }>;
  meta: Record<string, any>;
}

export interface PageError {
  hasError: boolean;
  errorCode: number | null;
  errorMessage: string;
  canRetry: boolean;
}

export interface Message {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  content: string;
  timestamp: number;
  read: boolean;
  actions?: Array<{ text: string; callback: () => void }>;
}

export interface Notification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  content: string;
  timestamp: number;
  duration?: number;
}

export interface FormValidation {
  isValidating: boolean;
  errors: Record<string, string[]>;
  touched: Record<string, boolean>;
  isValid: boolean;
}

export interface UIStateSummary {
  loading: {
    global: boolean;
    page: boolean;
    form: boolean;
    any: boolean;
  };
  dialogs: {
    open: boolean;
    count: number;
  };
  sidebar: {
    collapsed: boolean;
    width: number;
  };
  messages: {
    total: number;
    unread: number;
  };
  page: {
    name: string;
    hasError: boolean;
  };
  loginPage: {
    currentView: LoginView;
    isSubmitting: boolean;
    hasChanges: boolean;
  };
}

export const useUiStore = defineStore("ui", () => {
  const globalLoading: Ref<boolean> = ref(false);
  const loadingText: Ref<string> = ref("加载中...");
  const pageLoading: Ref<boolean> = ref(false);
  const formSaving: Ref<boolean> = ref(false);

  const theme: Ref<Theme> = ref("light");
  const primaryColor: Ref<string> = ref("#1890ff");

  const currentPage = reactive<CurrentPage>({
    name: "",
    title: "",
    breadcrumb: [],
    meta: {},
  });

  const pageError = reactive<PageError>({
    hasError: false,
    errorCode: null,
    errorMessage: "",
    canRetry: false,
  });

  const messages: Ref<Message[]> = ref([]);
  const notifications: Ref<Notification[]> = ref([]);
  const unreadCount: Ref<number> = ref(0);

  const formValidation = reactive<FormValidation>({
    isValidating: false,
    errors: {},
    touched: {},
    isValid: true,
  });

  const hasAnyLoading: ComputedRef<boolean> = computed(
    () => globalLoading.value || pageLoading.value || formSaving.value,
  );

  const setGlobalLoading = (
    loading: boolean,
    text: string = "加载中...",
  ): void => {
    globalLoading.value = loading;
    loadingText.value = text;
  };

  const setPageLoading = (loading: boolean): void => {
    pageLoading.value = loading;
  };

  const setFormSaving = (saving: boolean): void => {
    formSaving.value = saving;
  };

  const setPageError = (
    error: { code?: number; message: string; canRetry?: boolean } | null,
  ): void => {
    if (!error) {
      clearPageError();
      return;
    }
    pageError.hasError = true;
    pageError.errorCode = error.code || null;
    pageError.errorMessage = error.message;
    pageError.canRetry = error.canRetry || false;
  };

  const clearPageError = (): void => {
    pageError.hasError = false;
    pageError.errorCode = null;
    pageError.errorMessage = "";
    pageError.canRetry = false;
  };

  const setCurrentPage = (pageInfo: Partial<CurrentPage>): void => {
    Object.assign(currentPage, pageInfo);
  };

  const updateBreadcrumb = (
    breadcrumb: Array<{ name: string; path?: string }>,
  ): void => {
    currentPage.breadcrumb = breadcrumb;
  };

  const addMessage = (
    message: Omit<Message, "id" | "timestamp" | "read">,
  ): void => {
    const newMessage: Message = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      timestamp: Date.now(),
      read: false,
    };
    messages.value.unshift(newMessage);
    unreadCount.value += 1;

    if (messages.value.length > 100) {
      messages.value = messages.value.slice(0, 100);
    }
  };

  const markMessageAsRead = (messageId: string): void => {
    const message = messages.value.find((msg) => msg.id === messageId);
    if (message && !message.read) {
      message.read = true;
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
  };

  const markAllMessagesAsRead = (): void => {
    messages.value.forEach((message) => {
      message.read = true;
    });
    unreadCount.value = 0;
  };

  const clearMessages = (): void => {
    messages.value = [];
    notifications.value = [];
    unreadCount.value = 0;
  };

  const setFormValidation = (validation: Partial<FormValidation>): void => {
    Object.assign(formValidation, validation);
  };

  const resetFormState = (): void => {
    formValidation.isValidating = false;
    formValidation.errors = {};
    formValidation.touched = {};
    formValidation.isValid = true;
    formSaving.value = false;
  };

  const toggleTheme = (): void => {
    theme.value = theme.value === "light" ? "dark" : "light";
  };

  const setTheme = (newTheme: Theme): void => {
    theme.value = newTheme;
  };

  const setPrimaryColor = (color: string): void => {
    primaryColor.value = color;
  };

  const resetLoginPageState = (): void => {
    useLoginPageStore().resetLoginPageState();
  };

  const setLoginView = (view: LoginView): void => {
    useLoginPageStore().setLoginView(view);
  };

  const resetAllState = (): void => {
    setGlobalLoading(false);
    setPageLoading(false);
    setFormSaving(false);
    useDialogStore().closeAllDialogs();
    clearPageError();
    currentPage.name = "";
    currentPage.title = "";
    currentPage.breadcrumb = [];
    currentPage.meta = {};
    clearMessages();
    resetFormState();
    resetLoginPageState();
  };

  const getUIStateSummary = (): UIStateSummary => {
    const panelLayout = usePanelLayoutStore();
    const dialogStore = useDialogStore();
    const loginPageStore = useLoginPageStore();

    return {
      loading: {
        global: globalLoading.value,
        page: pageLoading.value,
        form: formSaving.value,
        any: hasAnyLoading.value,
      },
      dialogs: {
        open: dialogStore.hasOpenDialog,
        count: Object.values(dialogStore.dialogs).filter((v) => v === true)
          .length,
      },
      sidebar: {
        collapsed: panelLayout.sidebarCollapsed,
        width: panelLayout.actualSidebarWidth,
      },
      messages: {
        total: messages.value.length,
        unread: unreadCount.value,
      },
      page: {
        name: currentPage.name,
        hasError: pageError.hasError,
      },
      loginPage: {
        currentView: loginPageStore.loginPageState.currentView,
        isSubmitting: loginPageStore.loginPageState.loginForm.isSubmitting,
        hasChanges: loginPageStore.loginPageState.profileEdit.hasChanges,
      },
    };
  };

  return {
    globalLoading,
    loadingText,
    pageLoading,
    formSaving,
    theme,
    primaryColor,
    currentPage,
    pageError,
    messages,
    notifications,
    unreadCount,
    formValidation,
    hasAnyLoading,
    setGlobalLoading,
    setPageLoading,
    setFormSaving,
    setPageError,
    clearPageError,
    setCurrentPage,
    updateBreadcrumb,
    addMessage,
    markMessageAsRead,
    markAllMessagesAsRead,
    clearMessages,
    setFormValidation,
    resetFormState,
    toggleTheme,
    setTheme,
    setPrimaryColor,
    resetLoginPageState,
    setLoginView,
    resetAllState,
    getUIStateSummary,
  };
});
